import {
  parseReportFilterUrl,
  ReportFilterUrlError,
  reportFilterStateV1Schema,
  serializeReportFilterUrl,
  VideoSelectionUrlError,
  type FilterCatalog,
  type ReportFilterStateV1,
  type SavedView,
  type SubgroupCatalog,
} from "@meta-video/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  SessionExpiredError,
  PROTECTED_QUERY_KEY,
  type ApiRequestOptions,
} from "../../shared/api/client.js";
import {
  useAnalytics,
  useAnalyticsSessionId,
} from "../../shared/analytics/AnalyticsProvider.js";
import { analyticsEvents } from "../../shared/analytics/events.js";
import { useProductAnalytics } from "../../shared/analytics/useProductAnalytics.js";
import {
  notifySameDocumentSearchChange,
  subscribeToLocationSearchChanges,
} from "../../shared/url/locationSearch.js";
import {
  parseSavedViewSearch,
  removeSavedViewSearch,
  serializeSavedViewSearch,
  WorkspaceSearchError,
} from "../../shared/url/workspaceSearch.js";
import {
  getFilterCatalog,
  getSavedView,
  getSavedViews,
  SavedViewApiError,
} from "./api.js";
import {
  clearComparisonUrl,
  normalizeOversizedAuxiliarySearch,
  pruneVideoUrlState,
} from "../../shared/url/comparisonSearch.js";
import { removeVideoWorkspaceSearch } from "../../shared/url/workspaceSearch.js";

export type FilterStateSource = "catalog-default" | "saved" | "saved-default" | "url";
export type FilterBlockReason =
  | "empty-account-scope"
  | "empty-brand-scope"
  | "empty-subgroup-scope"
  | "invalid-url"
  | "load-error";

export interface FilterReadApi {
  readonly getFilterCatalog: (options?: ApiRequestOptions) => Promise<FilterCatalog>;
  readonly getSavedView: (
    viewId: string,
    options?: ApiRequestOptions,
  ) => Promise<SavedView>;
  readonly getSavedViews: (options?: ApiRequestOptions) => Promise<readonly SavedView[]>;
}

const defaultApi: FilterReadApi = {
  getFilterCatalog,
  getSavedView,
  getSavedViews,
};

interface FilterControllerBase {
  readonly activeView: SavedView | null;
  readonly adoptSavedView: (view: SavedView) => Promise<void>;
  readonly adoptSavedViewIfCurrent: (
    view: SavedView,
    expectedWorkspaceKey: string,
  ) => Promise<boolean>;
  readonly catalog: FilterCatalog | null;
  readonly disassociateSavedView: () => void;
  readonly disassociateSavedViewIfCurrent: (
    expectedAssociationId: string,
    expectedWorkspaceKey: string,
  ) => Promise<boolean>;
  readonly isDirty: boolean;
  readonly isPartial: boolean;
  readonly isShareReady: boolean;
  readonly loadWarnings: readonly string[];
  readonly notices: readonly string[];
  readonly openSavedView: (viewId: string) => void;
  readonly openingSavedViewId: string | null;
  readonly savedViews: readonly SavedView[];
  readonly savedViewsStatus: "error" | "loading" | "success";
  readonly selectSavedView: (viewId: string) => void;
  readonly setBrandIds: (brandIds: readonly string[]) => void;
  readonly setFilters: (filters: ReportFilterStateV1) => boolean;
  readonly subgroupCatalogs: Readonly<Record<string, SubgroupCatalog>>;
  readonly validationMessage: string | null;
  readonly refreshSavedViews: () => Promise<void>;
  readonly retryLoads: () => Promise<void>;
  readonly resetFilters: () => void;
  readonly updateSavedViewBaseline: (view: SavedView) => void;
  readonly workspaceMutationKey: string | null;
}

export type FilterStateController =
  | (FilterControllerBase & {
      readonly status: "loading";
      readonly source: FilterStateSource | null;
    })
  | (FilterControllerBase & {
      readonly draft: ReportFilterStateV1 | null;
      readonly error?: unknown;
      readonly message: string;
      readonly reason: FilterBlockReason;
      readonly source: FilterStateSource | null;
      readonly status: "blocked";
    })
  | (FilterControllerBase & {
      readonly filters: ReportFilterStateV1;
      readonly source: FilterStateSource;
      readonly status: "runnable";
    });

export interface UseFilterStateOptions {
  readonly api?: FilterReadApi;
  readonly navigate?: (href: string) => void;
  readonly onRunnable?: (filters: ReportFilterStateV1) => void;
}

interface Candidate {
  readonly filters: ReportFilterStateV1;
  readonly labels: SavedView["labels"] | null;
  readonly source: FilterStateSource;
  readonly view: SavedView | null;
}

type SavedViewContentBaseline = Pick<
  SavedView,
  "filters" | "id" | "labels" | "name" | "revision"
>;

interface CachedAuthorizedView {
  readonly superseded: boolean;
  readonly view: SavedView;
}

// The repository revision guards only name and filter storage (including its
// labels). Authorization, default, sharing, ownership, counts, and timestamps
// come from the latest successful read and must never be frozen with content.
function contentBaseline(view: SavedView): SavedViewContentBaseline {
  return {
    filters: view.filters,
    id: view.id,
    labels: view.labels,
    name: view.name,
    revision: view.revision,
  };
}

function withLoadedContent(
  current: SavedView,
  baseline: SavedViewContentBaseline,
): SavedView {
  return {
    ...current,
    filters: baseline.filters,
    labels: baseline.labels,
    name: baseline.name,
    revision: baseline.revision,
  };
}

interface IntersectionRunnable {
  readonly filters: ReportFilterStateV1;
  readonly kind: "runnable";
  readonly notices: readonly string[];
}

interface IntersectionBlocked {
  readonly draft: ReportFilterStateV1;
  readonly kind: "blocked";
  readonly message: string;
  readonly notices: readonly string[];
  readonly reason: Exclude<FilterBlockReason, "invalid-url" | "load-error">;
}

type IntersectionResult = IntersectionRunnable | IntersectionBlocked;

const INACCESSIBLE_SAVED_VIEW_NOTICE =
  "This Saved View is no longer available. Permitted report filters were retained.";
const INVALID_SAVED_VIEW_NOTICE =
  "The shared Saved View state was invalid and has been removed.";
const OVERSIZED_AUXILIARY_NOTICE =
  "Oversized shared video state was removed before the report was loaded.";
const UNAVAILABLE_AUXILIARY_NOTICE =
  "Unavailable shared video state was removed before the report was loaded.";
const NEWER_SAVED_VIEW_REVISION_NOTICE =
  "A newer Saved View revision is available. Reload it to use the latest saved filters.";
const RETIRED_FILTERS_NOTICE =
  "Removed playback status, objectives, and optimization goal selections because those filters are no longer available.";

function unique(values: readonly string[]): string[] {
  return [...new Set(values)];
}

function joinNames(values: readonly string[]): string {
  return new Intl.ListFormat("en", { style: "long", type: "conjunction" }).format(unique(values));
}

function labelForBrand(id: string, catalog: FilterCatalog, labels: SavedView["labels"] | null): string {
  return labels?.brands.find((brand) => brand.id === id)?.name
    ?? catalog.brands.find((brand) => brand.id === id)?.name
    ?? id;
}

function labelForAccount(id: string, catalog: FilterCatalog, labels: SavedView["labels"] | null): string {
  return labels?.accounts.find((account) => account.id === id)?.name
    ?? catalog.brands.flatMap((brand) => brand.accounts).find((account) => account.id === id)?.name
    ?? id;
}

function labelForSubgroupValue(
  brandId: string,
  groupId: string,
  valueId: string,
  labels: SavedView["labels"] | null,
): string {
  const group = labels?.subgroups.find(
    (candidate) => candidate.brandId === brandId && candidate.groupId === groupId,
  );
  return group?.values.find((value) => value.id === valueId)?.name ?? valueId;
}

function emptyBrandDraft(filters: ReportFilterStateV1): ReportFilterStateV1 {
  return {
    ...filters,
    accountIds: null,
    brandIds: [],
    groupSelections: [],
  };
}

export function intersectFilterState(
  filters: ReportFilterStateV1,
  catalog: FilterCatalog,
  subgroupCatalogs: Readonly<Record<string, SubgroupCatalog>>,
  labels: SavedView["labels"] | null = null,
): IntersectionResult {
  const hasRetiredFilters = filters.objectives.length > 0
    || filters.optimizationGoals.length > 0
    || filters.playStatuses.length > 0;
  const reconciledFilters = hasRetiredFilters
    ? {
        ...filters,
        objectives: [],
        optimizationGoals: [],
        playStatuses: [],
      }
    : filters;
  const notices = hasRetiredFilters ? [RETIRED_FILTERS_NOTICE] : [];

  if (reconciledFilters.brandIds.length === 0) {
    return {
      draft: emptyBrandDraft(reconciledFilters),
      kind: "blocked",
      message: "Select at least one brand before running this report.",
      notices,
      reason: "empty-brand-scope",
    };
  }

  const liveBrandIds = new Set(catalog.brands.map((brand) => brand.id));
  const brandIds = reconciledFilters.brandIds.filter((id) => liveBrandIds.has(id));
  const droppedBrandIds = reconciledFilters.brandIds.filter((id) => !liveBrandIds.has(id));
  if (brandIds.length === 0) {
    const names = droppedBrandIds.map((id) => labelForBrand(id, catalog, labels));
    return {
      draft: emptyBrandDraft(reconciledFilters),
      kind: "blocked",
      message: `This view refers to brands you no longer have access to: ${joinNames(names)}.`,
      notices,
      reason: "empty-brand-scope",
    };
  }

  if (droppedBrandIds.length > 0) {
    notices.push(
      `Removed brands you no longer have access to: ${joinNames(
        droppedBrandIds.map((id) => labelForBrand(id, catalog, labels)),
      )}.`,
    );
  }

  const selectedBrands = catalog.brands.filter((brand) => brandIds.includes(brand.id));
  const liveAccountIds = new Set(selectedBrands.flatMap((brand) => brand.accounts.map((account) => account.id)));
  let accountIds = reconciledFilters.accountIds;
  if (reconciledFilters.accountIds !== null) {
    accountIds = reconciledFilters.accountIds.filter((id) => liveAccountIds.has(id));
    const dropped = reconciledFilters.accountIds.filter((id) => !liveAccountIds.has(id));
    if (accountIds.length === 0) {
      return {
        draft: { ...reconciledFilters, accountIds: [], brandIds },
        kind: "blocked",
        message: `This view refers to ad accounts you no longer have access to: ${joinNames(
          dropped.map((id) => labelForAccount(id, catalog, labels)),
        )}.`,
        notices,
        reason: "empty-account-scope",
      };
    }
    if (dropped.length > 0) {
      notices.push(
        `Removed ad accounts you no longer have access to: ${joinNames(
          dropped.map((id) => labelForAccount(id, catalog, labels)),
        )}.`,
      );
    }
  }

  const groupSelections: ReportFilterStateV1["groupSelections"] = [];
  for (const selection of reconciledFilters.groupSelections) {
    if (!brandIds.includes(selection.brandId)) continue;
    const group = subgroupCatalogs[selection.brandId]?.find(
      (candidate) => candidate.id === selection.groupId,
    );
    if (!group) {
      const label = labels?.subgroups.find(
        (candidate) => candidate.brandId === selection.brandId
          && candidate.groupId === selection.groupId,
      )?.name ?? selection.groupId;
      return {
        draft: { ...reconciledFilters, accountIds, brandIds, groupSelections },
        kind: "blocked",
        message: `This view refers to a subgroup that is no longer available: ${label}.`,
        notices,
        reason: "empty-subgroup-scope",
      };
    }
    const liveValueIds = new Set(group.values.map((value) => value.id));
    const valueIds = selection.valueIds.filter((id) => liveValueIds.has(id));
    const dropped = selection.valueIds.filter((id) => !liveValueIds.has(id));
    if (valueIds.length === 0 && !selection.includeUnassigned) {
      return {
        draft: { ...reconciledFilters, accountIds, brandIds, groupSelections },
        kind: "blocked",
        message: `This view no longer has any available values in ${group.name}.`,
        notices,
        reason: "empty-subgroup-scope",
      };
    }
    if (dropped.length > 0) {
      notices.push(
        `Removed unavailable ${group.name} values: ${joinNames(
          dropped.map((id) => labelForSubgroupValue(
            selection.brandId,
            selection.groupId,
            id,
            labels,
          )),
        )}.`,
      );
    }
    groupSelections.push({ ...selection, valueIds });
  }

  return {
    filters: reportFilterStateV1Schema.parse({
      ...reconciledFilters,
      accountIds,
      brandIds,
      groupSelections,
    }),
    kind: "runnable",
    notices,
  };
}

export function cascadeBrandSelection(
  filters: ReportFilterStateV1,
  nextBrandIds: readonly string[],
  catalog: FilterCatalog,
): ReportFilterStateV1 {
  const brandIds = unique(nextBrandIds);
  if (brandIds.length === 0) return emptyBrandDraft(filters);

  const retainedAccountIds = filters.accountIds?.filter((accountId) =>
    catalog.brands.some(
      (brand) => brandIds.includes(brand.id)
        && brand.accounts.some((account) => account.id === accountId),
    ));
  const accountIds = retainedAccountIds === undefined || retainedAccountIds.length === 0
    ? null
    : retainedAccountIds;
  return {
    ...filters,
    accountIds,
    brandIds,
    groupSelections: filters.groupSelections.filter((selection) =>
      brandIds.includes(selection.brandId)),
  };
}

function catalogDefault(catalog: FilterCatalog): ReportFilterStateV1 {
  return reportFilterStateV1Schema.parse({
    accountIds: null,
    adNameSearch: null,
    brandIds: catalog.brands.map((brand) => brand.id),
    date: { preset: 30 },
    durationMax: null,
    durationMin: null,
    firstSeenFrom: null,
    firstSeenTo: null,
    groupSelections: [],
    objectives: [],
    optimizationGoals: [],
    playStatuses: [],
    version: 1,
  });
}

function canonical(filters: ReportFilterStateV1): string {
  return serializeReportFilterUrl(filters);
}

function filterWorkspaceHistoryKey(search: string): string {
  const parsed = parseReportFilterUrl(search);
  const filterKey = parsed.kind === "valid"
    ? `valid:${canonical(parsed.state)}`
    : parsed.kind;
  const workspace = parseSavedViewSearch(search);
  const savedViewKey = workspace.kind === "valid"
    ? workspace.savedViewId ?? "none"
    : "invalid";
  return `${filterKey}|savedView:${savedViewKey}`;
}

function workspaceMutationKey(
  search: string,
  pathname = window.location.pathname,
  hash = window.location.hash,
): string | null {
  const filters = parseReportFilterUrl(search);
  const workspace = parseSavedViewSearch(search);
  if (filters.kind !== "valid" || workspace.kind !== "valid") return null;
  try {
    const canonicalSearch = serializeSavedViewSearch(
      workspace.savedViewId,
      serializeReportFilterUrl(filters.state, search),
    );
    return `${pathname}${canonicalSearch === "" ? "" : `?${canonicalSearch}`}${hash}`;
  } catch (error) {
    if (
      error instanceof ReportFilterUrlError
      || error instanceof VideoSelectionUrlError
      || error instanceof WorkspaceSearchError
    ) return null;
    throw error;
  }
}

function brandSetKey(brandIds: readonly string[]): string {
  return JSON.stringify([...new Set(brandIds)].sort());
}

export function useFilterState(
  options: UseFilterStateOptions = {},
): FilterStateController {
  const api = options.api ?? defaultApi;
  const analytics = useAnalytics();
  const analyticsSessionId = useAnalyticsSessionId();
  const productAnalytics = useProductAnalytics();
  const queryClient = useQueryClient();
  const navigateRef = useRef(options.navigate);
  navigateRef.current = options.navigate;
  const onRunnableRef = useRef(options.onRunnable);
  onRunnableRef.current = options.onRunnable;
  const [search, setSearch] = useState(() => window.location.search);
  const searchRef = useRef(search);
  searchRef.current = search;
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [reconciledNotices, setReconciledNotices] = useState<readonly string[]>([]);
  const [sourceOverride, setSourceOverride] = useState<FilterStateSource | null>(null);
  const [newerRevisionViewId, setNewerRevisionViewId] = useState<string | null>(null);
  const [openingSavedViewId, setOpeningSavedViewId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const loadedViewRef = useRef<SavedViewContentBaseline | null>(null);
  const openRequestRef = useRef<{ readonly controller: AbortController; readonly token: symbol } | null>(null);
  const preflightAuxiliary = useMemo(
    () => normalizeOversizedAuxiliarySearch(search),
    [search],
  );
  const effectiveSearch = preflightAuxiliary.normalized
    ? preflightAuxiliary.search
    : search;
  const filterWorkspaceHistoryKeyRef = useRef(filterWorkspaceHistoryKey(effectiveSearch));

  useLayoutEffect(() => {
    if (!preflightAuxiliary.normalized) return;
    const nextUrl = `${window.location.pathname}${preflightAuxiliary.search === "" ? "" : `?${preflightAuxiliary.search}`}${window.location.hash}`;
    window.history.replaceState(window.history.state, "", nextUrl);
    filterWorkspaceHistoryKeyRef.current = filterWorkspaceHistoryKey(
      preflightAuxiliary.search,
    );
    setReconciledNotices((current) => unique([
      ...current,
      OVERSIZED_AUXILIARY_NOTICE,
    ]));
    searchRef.current = window.location.search;
    setSearch(window.location.search);
    notifySameDocumentSearchChange();
  }, [preflightAuxiliary.normalized, preflightAuxiliary.search]);

  useEffect(() => {
    const onLocationChange = () => {
      const nextSearch = window.location.search;
      const nextFilterWorkspaceHistoryKey = filterWorkspaceHistoryKey(nextSearch);
      if (nextFilterWorkspaceHistoryKey !== filterWorkspaceHistoryKeyRef.current) {
        setReconciledNotices([]);
        setSourceOverride(null);
      }
      filterWorkspaceHistoryKeyRef.current = nextFilterWorkspaceHistoryKey;
      setValidationMessage(null);
      searchRef.current = nextSearch;
      setSearch(nextSearch);
    };
    return subscribeToLocationSearchChanges(onLocationChange);
  }, []);

  const parsed = useMemo(() => parseReportFilterUrl(effectiveSearch), [effectiveSearch]);
  const workspace = useMemo(() => parseSavedViewSearch(effectiveSearch), [effectiveSearch]);
  const catalogQuery = useQuery({
    queryFn: ({ signal }) => api.getFilterCatalog({ signal }),
    queryKey: [...PROTECTED_QUERY_KEY, "filters", "catalog", api],
  });
  const savedViewsQueryKey = useMemo(
    () => [...PROTECTED_QUERY_KEY, "filters", "saved-views", api] as const,
    [api],
  );
  const savedViewsQuery = useQuery({
    queryFn: ({ signal }) => api.getSavedViews({ signal }),
    queryKey: savedViewsQueryKey,
  });
  const savedViews = savedViewsQuery.data ?? [];

  const cacheAuthorizedView = useCallback(async (
    view: SavedView,
  ): Promise<CachedAuthorizedView> => {
    await queryClient.cancelQueries({ exact: true, queryKey: savedViewsQueryKey });
    let cached: CachedAuthorizedView = { superseded: false, view };
    queryClient.setQueryData<readonly SavedView[]>(savedViewsQueryKey, (current = []) => {
      const present = current.find((candidate) => candidate.id === view.id);
      if (present && present.revision > view.revision) {
        cached = { superseded: true, view: present };
        return current;
      }
      return present
        ? current.map((candidate) => candidate.id === view.id ? view : candidate)
        : [...current, view];
    });
    return cached;
  }, [queryClient, savedViewsQueryKey]);

  const removeCachedView = useCallback(async (viewId: string) => {
    await queryClient.cancelQueries({ exact: true, queryKey: savedViewsQueryKey });
    queryClient.setQueryData<readonly SavedView[]>(savedViewsQueryKey, (current = []) =>
      current.filter((view) => view.id !== viewId));
  }, [queryClient, savedViewsQueryKey]);
  const associatedViewId = workspace.kind === "valid" ? workspace.savedViewId : null;
  const activeView = associatedViewId === null
    ? null
    : savedViews.find((view) => view.id === associatedViewId) ?? null;
  const inaccessibleAssociation = workspace.kind === "valid"
    && associatedViewId !== null
    && savedViewsQuery.data !== undefined
    && activeView === null;
  if (
    activeView !== null
    && (loadedViewRef.current === null || loadedViewRef.current.id !== activeView.id)
  ) {
    loadedViewRef.current = contentBaseline(activeView);
  }

  useEffect(() => {
    if (activeView === null) {
      if (associatedViewId === null) {
        loadedViewRef.current = null;
        setNewerRevisionViewId(null);
      }
      return;
    }
    const loaded = loadedViewRef.current;
    if (loaded === null || loaded.id !== activeView.id) {
      setNewerRevisionViewId(null);
      return;
    }
    if (activeView.revision > loaded.revision) {
      setNewerRevisionViewId(activeView.id);
    }
  }, [activeView, associatedViewId]);

  const loadedActiveView = activeView === null
    ? null
    : loadedViewRef.current?.id === activeView.id
      ? withLoadedContent(activeView, loadedViewRef.current)
      : activeView;
  let candidate: Candidate | null = null;
  if (workspace.kind === "valid" && parsed.kind === "valid") {
    candidate = {
      filters: parsed.state,
      labels: loadedActiveView?.labels ?? null,
      source: sourceOverride ?? (activeView ? "saved" : "url"),
      view: loadedActiveView,
    };
  } else if (
    workspace.kind === "valid"
    &&
    parsed.kind === "absent"
    && catalogQuery.data
    && savedViewsQuery.data
  ) {
    const requestedView = associatedViewId === null
      ? null
      : savedViewsQuery.data.find((view) => view.id === associatedViewId) ?? null;
    const defaultView = requestedView === null
      ? savedViewsQuery.data.find((view) => view.isDefault) ?? null
      : null;
    const selectedView = requestedView ?? defaultView;
    candidate = selectedView
      ? {
          filters: selectedView.filters,
          labels: selectedView.labels,
          source: requestedView ? "saved" : "saved-default",
          view: requestedView ?? (associatedViewId === null ? selectedView : null),
        }
      : {
          filters: catalogDefault(catalogQuery.data),
          labels: null,
          source: "catalog-default",
          view: null,
        };
  }

  const subgroupCatalogs = Object.fromEntries(
    (catalogQuery.data?.subgroups ?? []).map(({ brandId, groups }) => [brandId, groups]),
  );
  const loadWarnings: string[] = [];
  if (
    parsed.kind === "valid"
    && savedViewsQuery.error
    && !(savedViewsQuery.error instanceof SessionExpiredError)
  ) {
    loadWarnings.push("Saved views could not be loaded.");
  }

  const writeFilters = useCallback((
    filters: ReportFilterStateV1,
    options: Readonly<{
      replace?: boolean;
      savedViewId?: string | null;
    }> = {},
  ): boolean => {
    const { replace = false } = options;
    try {
      const currentFilters = parseReportFilterUrl(window.location.search);
      const brandSetChanged = currentFilters.kind !== "valid"
        || brandSetKey(currentFilters.state.brandIds) !== brandSetKey(filters.brandIds);
      const existingSearch = brandSetChanged
        ? pruneVideoUrlState(window.location.search, filters.brandIds)
        : window.location.search;
      const filterSearch = serializeReportFilterUrl(filters, existingSearch);
      const nextSearch = Object.hasOwn(options, "savedViewId")
        ? serializeSavedViewSearch(options.savedViewId ?? null, filterSearch)
        : filterSearch;
      if (nextSearch === window.location.search.replace(/^\?/, "")) {
        setValidationMessage(null);
        return true;
      }
      const nextUrl = `${window.location.pathname}?${nextSearch}${window.location.hash}`;
      window.history[replace ? "replaceState" : "pushState"](
        window.history.state,
        "",
        nextUrl,
      );
      filterWorkspaceHistoryKeyRef.current = filterWorkspaceHistoryKey(nextSearch);
      searchRef.current = window.location.search;
      setSearch(window.location.search);
      notifySameDocumentSearchChange();
      setValidationMessage(null);
      return true;
    } catch (error) {
      if (
        !(error instanceof ReportFilterUrlError)
        && !(error instanceof VideoSelectionUrlError)
        && !(error instanceof WorkspaceSearchError)
      ) throw error;
      setValidationMessage(
        "These filters are too large or invalid to share. Shorten the filter values and try again.",
      );
      return false;
    }
  }, []);

  const writeUserFilters = useCallback((filters: ReportFilterStateV1): boolean => {
    return writeFilters(filters);
  }, [writeFilters]);

  const setFilters = useCallback((filters: ReportFilterStateV1): boolean => {
    const accepted = writeUserFilters(filters);
    if (accepted) {
      setReconciledNotices((current) => current.length === 0 ? current : []);
    }
    return accepted;
  }, [writeUserFilters]);

  const selectSavedView = useCallback((viewId: string) => {
    const view = savedViews.find((candidateView) => candidateView.id === viewId);
    if (!view) return;
    if (!writeFilters(view.filters, { savedViewId: view.id })) return;
    loadedViewRef.current = contentBaseline(view);
    setNewerRevisionViewId(null);
    setReconciledNotices([]);
    setSourceOverride("saved");
    productAnalytics.savedViewAction("open");
  }, [productAnalytics, savedViews, writeFilters]);

  const openSavedView = useCallback((viewId: string) => {
    if (!savedViews.some((candidateView) => candidateView.id === viewId)) return;
    openRequestRef.current?.controller.abort();
    const controller = new AbortController();
    const token = Symbol(viewId);
    openRequestRef.current = { controller, token };
    setOpeningSavedViewId(viewId);
    const submittedLocation = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    setActionError(null);
    setValidationMessage(null);
    void api.getSavedView(viewId, { signal: controller.signal })
      .then(async (view) => {
        if (
          controller.signal.aborted
          || openRequestRef.current?.token !== token
          || `${window.location.pathname}${window.location.search}${window.location.hash}` !== submittedLocation
        ) return;
        if (view.id !== viewId) {
          setValidationMessage("This Saved View could not be opened. Try again.");
          return;
        }
        try {
          const cached = await cacheAuthorizedView(view);
          if (
            controller.signal.aborted
            || openRequestRef.current?.token !== token
            || `${window.location.pathname}${window.location.search}${window.location.hash}` !== submittedLocation
          ) return;
          const openedView = cached.view;
          const withoutVideo = removeVideoWorkspaceSearch(
            clearComparisonUrl(window.location.search),
          );
          const filterSearch = serializeReportFilterUrl(openedView.filters, withoutVideo);
          const nextSearch = serializeSavedViewSearch(openedView.id, filterSearch);
          const nextUrl = `/${nextSearch === "" ? "" : `?${nextSearch}`}${window.location.hash}`;
          loadedViewRef.current = contentBaseline(openedView);
          setNewerRevisionViewId(null);
          setReconciledNotices([]);
          setSourceOverride("saved");
          setValidationMessage(null);
          if (navigateRef.current) {
            filterWorkspaceHistoryKeyRef.current = filterWorkspaceHistoryKey(nextSearch);
            searchRef.current = `?${nextSearch}`;
            setSearch(searchRef.current);
            navigateRef.current(nextUrl);
          } else {
            window.history.pushState(window.history.state, "", nextUrl);
            filterWorkspaceHistoryKeyRef.current = filterWorkspaceHistoryKey(nextSearch);
            searchRef.current = window.location.search;
            setSearch(window.location.search);
            notifySameDocumentSearchChange();
          }
          productAnalytics.savedViewAction("open");
        } catch (error) {
          if (
            !(error instanceof ReportFilterUrlError)
            && !(error instanceof VideoSelectionUrlError)
            && !(error instanceof WorkspaceSearchError)
          ) throw error;
          setValidationMessage(
            "This Saved View is too large or invalid to open. Shorten its filters and try again.",
          );
        }
      })
      .catch(async (error: unknown) => {
        if (
          controller.signal.aborted
          || openRequestRef.current?.token !== token
          || `${window.location.pathname}${window.location.search}${window.location.hash}` !== submittedLocation
        ) return;
        if (error instanceof SessionExpiredError) {
          setActionError(error);
        } else if (
          error instanceof SavedViewApiError
          && error.code === "SAVED_VIEW_NOT_FOUND"
        ) {
          await removeCachedView(viewId);
          if (
            controller.signal.aborted
            || openRequestRef.current?.token !== token
            || `${window.location.pathname}${window.location.search}${window.location.hash}` !== submittedLocation
          ) return;
          setValidationMessage("This Saved View is no longer available.");
        } else {
          setValidationMessage("This Saved View could not be opened. Try again.");
        }
      })
      .finally(() => {
        if (openRequestRef.current?.token === token) {
          openRequestRef.current = null;
          setOpeningSavedViewId(null);
        }
      });
  }, [
    api,
    cacheAuthorizedView,
    productAnalytics,
    removeCachedView,
    savedViews,
  ]);

  useEffect(() => () => openRequestRef.current?.controller.abort(), []);

  const currentWorkspaceMutationKey = useCallback((): string | null => {
    return workspaceMutationKey(window.location.search);
  }, []);

  const applyAuthorizedAdoption = useCallback((
    submittedView: SavedView,
    cached: CachedAuthorizedView,
  ): boolean => {
    const preservesLoadedBaseline = cached.superseded
      && loadedViewRef.current?.id === submittedView.id;
    if (preservesLoadedBaseline) {
      const currentFilters = parseReportFilterUrl(window.location.search);
      if (currentFilters.kind !== "valid") return false;
      if (!writeFilters(currentFilters.state, { savedViewId: submittedView.id })) return false;
      setNewerRevisionViewId(submittedView.id);
    } else {
      if (!writeFilters(cached.view.filters, { savedViewId: cached.view.id })) return false;
      loadedViewRef.current = contentBaseline(cached.view);
      setNewerRevisionViewId(null);
      setReconciledNotices([]);
    }
    setSourceOverride("saved");
    return true;
  }, [writeFilters]);

  const adoptSavedView = useCallback(async (view: SavedView) => {
    const cached = await cacheAuthorizedView(view);
    applyAuthorizedAdoption(view, cached);
  }, [applyAuthorizedAdoption, cacheAuthorizedView]);

  const adoptSavedViewIfCurrent = useCallback(async (
    view: SavedView,
    expectedWorkspaceKey: string,
  ): Promise<boolean> => {
    if (currentWorkspaceMutationKey() !== expectedWorkspaceKey) return false;
    const cached = await cacheAuthorizedView(view);
    if (currentWorkspaceMutationKey() !== expectedWorkspaceKey) return false;
    return applyAuthorizedAdoption(view, cached);
  }, [applyAuthorizedAdoption, cacheAuthorizedView, currentWorkspaceMutationKey]);

  const updateSavedViewBaseline = useCallback((view: SavedView) => {
    if (loadedViewRef.current?.id !== view.id) return;
    const liveRevision = savedViews.find((candidateView) => candidateView.id === view.id)?.revision
      ?? 0;
    if (
      (newerRevisionViewId === view.id || liveRevision > loadedViewRef.current.revision)
      && view.revision <= liveRevision
    ) return;
    if (view.revision < Math.max(loadedViewRef.current.revision, liveRevision)) return;
    loadedViewRef.current = contentBaseline(view);
    setNewerRevisionViewId(null);
  }, [newerRevisionViewId, savedViews]);

  const disassociateSavedView = useCallback(() => {
    if (!candidate || !writeFilters(candidate.filters, { savedViewId: null })) return;
    setReconciledNotices([]);
    setSourceOverride("url");
  }, [candidate, writeFilters]);

  const disassociateSavedViewIfCurrent = useCallback(async (
    expectedAssociationId: string,
    expectedWorkspaceKey: string,
  ): Promise<boolean> => {
    if (currentWorkspaceMutationKey() !== expectedWorkspaceKey) return false;
    const currentAssociation = parseSavedViewSearch(window.location.search);
    if (
      currentAssociation.kind !== "valid"
      || currentAssociation.savedViewId !== expectedAssociationId
    ) return false;
    const currentFilters = parseReportFilterUrl(window.location.search);
    if (currentFilters.kind !== "valid") return false;
    if (!writeFilters(currentFilters.state, { savedViewId: null })) return false;
    await removeCachedView(expectedAssociationId);
    setReconciledNotices([]);
    setSourceOverride("url");
    return true;
  }, [currentWorkspaceMutationKey, removeCachedView, writeFilters]);

  const resetFilters = useCallback(() => {
    if (!catalogQuery.data) return;
    const defaults = catalogDefault(catalogQuery.data);
    if (!writeFilters(defaults, { savedViewId: null })) return;
    if (
      analyticsSessionId &&
      candidate &&
      canonical(candidate.filters) !== canonical(defaults)
    ) {
      analytics.capture(
        analyticsEvents.filtersReset(analyticsSessionId, {
          changedFields: ["all"],
          filters: defaults,
        }),
      );
    }
    setReconciledNotices([]);
    setSourceOverride("catalog-default");
  }, [
    analytics,
    analyticsSessionId,
    candidate,
    catalogQuery.data,
    writeFilters,
  ]);

  const base = {
    activeView: candidate?.view ?? activeView,
    adoptSavedView,
    adoptSavedViewIfCurrent,
    catalog: catalogQuery.data ?? null,
    disassociateSavedView,
    disassociateSavedViewIfCurrent,
    isDirty: false,
    isPartial: false,
    isShareReady: workspace.kind === "valid"
      && (
        workspace.savedViewId === null
        || (savedViewsQuery.isSuccess && activeView !== null)
      ),
    loadWarnings,
    notices: [] as readonly string[],
    openSavedView,
    openingSavedViewId,
    refreshSavedViews: async () => {
      await savedViewsQuery.refetch();
    },
    retryLoads: async () => {
      await Promise.all([
        catalogQuery.refetch(),
        savedViewsQuery.refetch(),
      ]);
    },
    resetFilters,
    savedViews,
    savedViewsStatus: savedViewsQuery.isPending
      ? "loading"
      : savedViewsQuery.isError
        ? "error"
        : "success",
    selectSavedView,
    setBrandIds: (brandIds: readonly string[]) => {
      if (!candidate || !catalogQuery.data) return;
      setFilters(cascadeBrandSelection(candidate.filters, brandIds, catalogQuery.data));
    },
    setFilters,
    subgroupCatalogs,
    updateSavedViewBaseline,
    validationMessage,
    workspaceMutationKey: workspaceMutationKey(effectiveSearch),
  } satisfies FilterControllerBase;

  const sessionExpiredError = [
    catalogQuery.error,
    savedViewsQuery.error,
  ].find((error) => error instanceof SessionExpiredError);
  const authLikeError = sessionExpiredError
    ?? actionError
    ?? catalogQuery.error
    ?? (parsed.kind === "absent" ? savedViewsQuery.error : null);
  let result: FilterStateController;
  if (authLikeError) {
    result = {
      ...base,
      draft: candidate?.filters ?? null,
      error: authLikeError,
      message: "Filters could not be loaded. Try again.",
      reason: "load-error",
      source: candidate?.source ?? null,
      status: "blocked",
    };
  } else if (workspace.kind === "invalid" || parsed.kind === "invalid") {
    result = {
      ...base,
      draft: null,
      message: "These shared filters are invalid and were not run.",
      reason: "invalid-url",
      source: "url",
      status: "blocked",
    };
  } else if (
    !catalogQuery.data
    || (parsed.kind === "absent" && !savedViewsQuery.data)
    || !candidate
  ) {
    result = { ...base, source: candidate?.source ?? null, status: "loading" };
  } else {
    const intersection = intersectFilterState(
      candidate.filters,
      catalogQuery.data,
      subgroupCatalogs,
      candidate.labels,
    );
    const activeIntersection = candidate.view
      ? intersectFilterState(
          candidate.view.filters,
          catalogQuery.data,
          subgroupCatalogs,
          candidate.view.labels,
        )
      : null;
    const contextNotices = activeIntersection?.notices ?? [];
    const notices = unique([
      ...intersection.notices,
      ...contextNotices,
      ...reconciledNotices,
      ...(newerRevisionViewId === candidate.view?.id
        ? [NEWER_SAVED_VIEW_REVISION_NOTICE]
        : []),
    ]);
    const isPartial = notices.length > 0 || activeIntersection?.kind === "blocked";
    const currentFilters = intersection.kind === "runnable" ? intersection.filters : null;
    const isDirty = candidate.view !== null && (
      currentFilters === null
      || canonical(currentFilters) !== canonical(candidate.view.filters)
      || newerRevisionViewId === candidate.view.id
    );
    result = intersection.kind === "blocked"
      ? {
          ...base,
          draft: intersection.draft,
          isDirty,
          isPartial,
          message: intersection.message,
          notices,
          reason: intersection.reason,
          source: candidate.source,
          status: "blocked",
        }
      : {
          ...base,
          filters: intersection.filters,
          isDirty,
          isPartial,
          notices,
          source: candidate.source,
          status: "runnable",
        };
  }

  if (
    inaccessibleAssociation
    && (
      result.status === "runnable"
      || (result.status === "blocked"
        && result.reason !== "invalid-url"
        && result.reason !== "load-error")
    )
  ) {
    result = {
      ...result,
      isPartial: true,
      notices: unique([...result.notices, INACCESSIBLE_SAVED_VIEW_NOTICE]),
    };
  }

  const resolvedResult = result;
  const currentWorkspaceSearch = window.location.search.replace(/^\?/, "");
  const candidateKey = candidate ? canonical(candidate.filters) : null;
  let normalizedWorkspaceSearch: string | null = null;
  let normalizedWorkspaceNotices: readonly string[] = [];
  try {
    if (resolvedResult.status === "runnable") {
      const pruned = pruneVideoUrlState(
        currentWorkspaceSearch,
        resolvedResult.filters.brandIds,
      );
      const auxiliaryStateChanged = pruned !== currentWorkspaceSearch;
      const associationNormalized = inaccessibleAssociation
        ? removeSavedViewSearch(pruned)
        : pruned;
      if (parsed.kind === "absent") {
        const withFilters = serializeReportFilterUrl(
          resolvedResult.filters,
          associationNormalized,
        );
        normalizedWorkspaceSearch = serializeSavedViewSearch(
          inaccessibleAssociation ? null : candidate?.view?.id ?? null,
          withFilters,
        );
      } else if (candidateKey !== canonical(resolvedResult.filters)) {
        normalizedWorkspaceSearch = serializeReportFilterUrl(
          resolvedResult.filters,
          associationNormalized,
        );
      } else {
        normalizedWorkspaceSearch = associationNormalized;
      }
      normalizedWorkspaceNotices = unique([
        ...resolvedResult.notices,
        ...(auxiliaryStateChanged ? [UNAVAILABLE_AUXILIARY_NOTICE] : []),
      ]);
    } else if (
      resolvedResult.status === "blocked"
      && resolvedResult.reason !== "invalid-url"
      && resolvedResult.reason !== "load-error"
      && resolvedResult.draft !== null
    ) {
      const pruned = pruneVideoUrlState(
        currentWorkspaceSearch,
        resolvedResult.draft.brandIds,
      );
      const auxiliaryStateChanged = pruned !== currentWorkspaceSearch;
      normalizedWorkspaceSearch = inaccessibleAssociation
        ? removeSavedViewSearch(pruned)
        : pruned;
      normalizedWorkspaceNotices = unique([
        ...resolvedResult.notices,
        ...(auxiliaryStateChanged ? [UNAVAILABLE_AUXILIARY_NOTICE] : []),
      ]);
    }
  } catch (error) {
    if (
      !(error instanceof ReportFilterUrlError)
      && !(error instanceof VideoSelectionUrlError)
      && !(error instanceof WorkspaceSearchError)
    ) throw error;
  }
  const needsWorkspaceNormalization = normalizedWorkspaceSearch !== null
    && normalizedWorkspaceSearch !== currentWorkspaceSearch;
  const normalizationNoticeKey = normalizedWorkspaceNotices.join("\0");

  useLayoutEffect(() => {
    if (!needsWorkspaceNormalization || normalizedWorkspaceSearch === null) return;
    const nextUrl = `${window.location.pathname}${normalizedWorkspaceSearch === "" ? "" : `?${normalizedWorkspaceSearch}`}${window.location.hash}`;
    window.history.replaceState(window.history.state, "", nextUrl);
    filterWorkspaceHistoryKeyRef.current = filterWorkspaceHistoryKey(
      normalizedWorkspaceSearch,
    );
    setSearch(window.location.search);
    notifySameDocumentSearchChange();
    setReconciledNotices(normalizedWorkspaceNotices);
    setSourceOverride(candidate?.source ?? null);
  }, [
    candidate?.source,
    needsWorkspaceNormalization,
    normalizationNoticeKey,
    normalizedWorkspaceSearch,
  ]);

  if (needsWorkspaceNormalization && resolvedResult.status === "runnable") {
    result = {
      ...base,
      source: resolvedResult.source,
      status: "loading",
    };
  }

  const runnableKey = result.status === "runnable" ? canonical(result.filters) : null;
  useEffect(() => {
    if (runnableKey === null || result.status !== "runnable") return;
    onRunnableRef.current?.(result.filters);
  }, [runnableKey]);

  useEffect(() => {
    if (parsed.kind !== "absent" || result.status !== "runnable") return;
    setReconciledNotices(result.notices);
    setSourceOverride(candidate?.source ?? null);
    writeFilters(result.filters, {
      replace: true,
      savedViewId: candidate?.view?.id ?? null,
    });
  }, [candidate?.source, candidate?.view?.id, parsed.kind, result.status, writeFilters]);

  useEffect(() => {
    if (
      parsed.kind !== "valid"
      || result.status !== "runnable"
      || candidateKey === runnableKey
    ) return;
    setReconciledNotices(result.notices);
    writeFilters(result.filters, { replace: true });
  }, [candidateKey, parsed.kind, result.status, runnableKey, writeFilters]);

  useEffect(() => {
    if (workspace.kind !== "invalid") return;
    const nextSearch = removeSavedViewSearch(window.location.search);
    const nextUrl = `${window.location.pathname}${nextSearch === "" ? "" : `?${nextSearch}`}${window.location.hash}`;
    window.history.replaceState(window.history.state, "", nextUrl);
    filterWorkspaceHistoryKeyRef.current = filterWorkspaceHistoryKey(nextSearch);
    setSearch(window.location.search);
    notifySameDocumentSearchChange();
    setReconciledNotices([INVALID_SAVED_VIEW_NOTICE]);
    setSourceOverride("url");
  }, [workspace.kind]);

  return result;
}
